import db from "../db/database";
import { getCurrentDateTimeISO } from "../utils/dateUtils";
import { sendToBusinessCentral } from "../utils/businessCentralUtils";
import { getCierreDetalles } from "./cierreDeCajaService";
import { getAperturaDeCajaById } from "./aperturaDeCajaService";
// Transformar cierre de caja para enviarlo a Business Central
export const transformCierreDeCajaForBC = async (cierre) => {
	// Crear objeto de cabecera de cierre según formato API BC

	const cajaActiva = await getAperturaDeCajaById(cierre.idApertura);

	if (!cajaActiva) {
		throw new Error("No se pudo obtener la caja activa");
	}

	if (!cajaActiva.No) {
		throw new Error(
			"No se pudo obtener el número de la caja activa, falta sincronizar la caja"
		);
	}

	const cierreHeader = {
		Cajero: cierre.Cajero || "",
		ImporteDeApertura: cierre.ImporteDeApertura || 0,
		FechaDeApertura:
			cierre.FechaDeApertura || getCurrentDateTimeISO().split("T")[0],
		ImporteDeCierreBS: cierre.ImporteDeCierreBS || 0,
		ImporteDeCierreUS: cierre.ImporteDeCierreUS || 0,
		ImporteDeCierreEUR: cierre.ImporteDeCierreEUR || 0,
		ArqueoBS: cierre.ArqueoBS || 0,
		ArqueoUS: cierre.ArqueoUS || 0,
		ArqueoEUR: cierre.ArqueoEUR || 0,
		ArqueoEfectivoUS: cierre.ArqueoEfectivoUS || 0,
		ArqueoTarjetaUS: cierre.ArqueoTarjetaUS || 0,
		FechaDeCierre:
			cierre.FechaDeCierre || getCurrentDateTimeISO().split("T")[0],
		Estado: cierre.Estado || "Abierto",
		idApertura: cajaActiva.No,
		Comprobante_Tarjetas: cierre.Comprobante_Tarjetas || "",
	};

	// Filtrar campos con valores vacíos
	return Object.fromEntries(
		Object.entries(cierreHeader).filter(([_, value]) => value !== "")
	);
};

// Transformar detalle de cierre para enviarlo a Business Central
export const transformCierreDetalleForBC = (
	detalle,
	documentNo,
	idApertura,
	index
) => {
	// Crear objeto de línea de cierre según formato API BC
	const line = {
		Document_No_: documentNo,
		Line_No_: detalle.Line_No || (index + 1) * 10000,
		idCierre: documentNo,
		idApertura: idApertura || detalle.idApertura,
		idFormaPago: detalle.idFormaPago || "",
		MontoPago: detalle.MontoPago || 0,
	};

	// Filtrar campos con valores vacíos
	return Object.fromEntries(
		Object.entries(line).filter(([_, value]) => value !== "")
	);
};

// Sincronizar un cierre de caja a Business Central
export const syncCierreDeCajaToBC = async (cierre) => {
	try {
		// 1. Preparar la cabecera del cierre para BC
		const cierreForBC = await transformCierreDeCajaForBC(cierre);
		const cierreData = {
			data: JSON.stringify({ Cierres: [cierreForBC] }),
		};

		// 2. Enviar la cabecera a Business Central
		const headerResponse = await sendToBusinessCentral(
			"Importaciones_insertaCierreCaja",
			cierreData,
			true
		);

		// 3. Obtener número de documento de la respuesta
		const documentNo = headerResponse.value;
		if (!documentNo) {
			throw new Error(
				"No se pudo obtener el número de documento del cierre de caja"
			);
		}

		// 4. Actualizar el cierre local con el número asignado por BC
		await db.cierreDeCaja.update(cierre.id, {
			No: documentNo, // Guardamos el número que nos devuelve BC
			lastSynced: getCurrentDateTimeISO(),
			pendingSync: 0,
		});

		// 5. Obtener los detalles del cierre
		const cierreDetalles = await getCierreDetalles(cierre.id);

		// 6. Preparar los detalles para BC
		const transformedDetalles = cierreDetalles.map((detalle, index) =>
			transformCierreDetalleForBC(detalle, documentNo, cierre.idApertura, index)
		);

		// Si no hay detalles guardados, obtengamos los datos de pago directamente de la base de datos
		if (transformedDetalles.length === 0) {
			// Consultar si hay pagos temporales guardados en la tabla
			const tempDetalles = await db.cierreDeCajaDetalle
				.where("cierreId")
				.equals(cierre.id)
				.toArray();

			if (tempDetalles.length > 0) {
				// Usar estos detalles para sincronizar
				transformedDetalles.push(
					...tempDetalles.map((detalle, index) =>
						transformCierreDetalleForBC(
							detalle,
							documentNo,
							cierre.idApertura,
							index
						)
					)
				);
			}
		}

		// 7. Enviar los detalles a Business Central sólo si hay detalles para enviar
		if (transformedDetalles.length > 0) {
			const detallesData = {
				data: JSON.stringify({ CierreDetalles: transformedDetalles }),
			};

			await sendToBusinessCentral(
				"Importaciones_insertaCierreDetalle",
				detallesData,
				true
			);

			// 8. Actualizar estado de sincronización de detalles
			for (const detalle of cierreDetalles) {
				await db.cierreDeCajaDetalle.update(detalle.id, {
					lastSynced: getCurrentDateTimeISO(),
					pendingSync: 0,
				});
			}
		} else {
			console.warn(
				"No hay detalles para sincronizar para el cierre",
				documentNo
			);
		}

		return {
			success: true,
			message: "Cierre de caja sincronizado correctamente",
		};
	} catch (error) {
		console.error("Error al sincronizar cierre de caja:", error);
		return { success: false, message: error.message };
	}
};

// Sincronizar todos los cierres de caja pendientes
export const syncCierresDeCaja = async () => {
	try {
		// Obtener cierres pendientes de sincronización
		const pendingCierres = await db.cierreDeCaja
			.where("pendingSync")
			.equals(1)
			.toArray();

		const results = [];

		// Sincronizar cada cierre pendiente
		for (const cierre of pendingCierres) {
			const result = await syncCierreDeCajaToBC(cierre);
			results.push({
				id: cierre.id,
				No: cierre.No,
				success: result.success,
				message: result.message,
			});
		}

		return results;
	} catch (error) {
		console.error("Error al sincronizar cierres de caja:", error);
		throw error;
	}
};
